import { formatHumanName, resolveId } from '@medplum/core'
import type {
  Location,
  QuestionnaireResponse,
  Reference,
  Resource,
} from '@medplum/fhirtypes'
import { endOfDay, format, startOfDay } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { chunk, compact, groupBy, maxBy, sortBy, uniq } from 'lodash-es'
import { z } from 'zod'
import type { SeenMedplumClient } from '#src/client.ts'
import * as codeSystems from '#src/codeSystems.ts'
import { type DateString, parseDateString } from '#src/dates.ts'
import { invariant } from '#src/invariant.ts'
import { ExtendedAppointment } from '#src/models/appointments/appointment.ts'
import * as patients from '#src/models/patients/index.ts'
import {
  buildingAddressKey,
  getPrimaryPhoneNumber,
  homeAddressOrFallback,
} from '#src/models/patients/index.ts'
import { Participant } from '#src/models/patients/participant.ts'
import { ServiceAllocationQuestionnaire } from '#src/models/questionnaires/definitions/patient/ServiceAllocation.questionnaire.ts'
import { normalizePhone } from '#src/phone.ts'
import { tryGetQuestionnaireAnswers } from '#src/questionnaire.ts'
import { getReferenceString } from '#src/references.ts'
import { normalizeQuery } from '#src/search.ts'
import { getType, officialNameOrFallback } from '#src/utils.ts'

export type TransportManifestDate = Date | DateString

interface TransportManifestClock {
  day: string
  start: string
  end: string
  format: (date: Date | string, pattern: string) => string
}

function createTransportManifestClock(
  date: TransportManifestDate,
  timeZone: string | undefined
): TransportManifestClock {
  if (timeZone) {
    const day =
      typeof date === 'string'
        ? date
        : formatInTimeZone(date, timeZone, 'yyyy-MM-dd')
    return {
      day,
      start: fromZonedTime(`${day}T00:00:00.000`, timeZone).toISOString(),
      end: fromZonedTime(`${day}T23:59:59.999`, timeZone).toISOString(),
      format: (value, pattern) => formatInTimeZone(value, timeZone, pattern),
    }
  }
  const localDate = typeof date === 'string' ? parseDateString(date) : date
  return {
    day: format(localDate, 'yyyy-MM-dd'),
    start: startOfDay(localDate).toISOString(),
    end: endOfDay(localDate).toISOString(),
    format: (value, pattern) => format(value, pattern),
  }
}

// Extract only the first 5 digits from a postal code, ignoring ZIP+4 extensions.
// Returns empty string if the postal code doesn't have at least 5 digits.
const normalizeZipCode = (postalCode: string | undefined): string => {
  if (!postalCode) {
    return ''
  }
  const digits = postalCode.replace(/\D/g, '')
  return digits.length >= 5 ? digits.slice(0, 5) : ''
}

const _transportManifestEntrySchema = z.object({
  date: z.string(),
  participantId: z.string(),
  participantName: z.string(),
  participantPhone: z.string(),
  appointmentPurpose: z.string(),
  originAddress: z.string(),
  originUnit: z.string(),
  originCity: z.string(),
  originState: z.string(),
  originZip: z.string(),
  destinationAddress: z.string(),
  destinationUnit: z.string(),
  destinationCity: z.string(),
  destinationState: z.string(),
  destinationZip: z.string(),
  assistiveDevice: z.string(),
  wheelchairCount: z.number(),
  passengerCount: z.number(),
  scheduledPickupTime: z.string(),
  appointmentTime: z.string(),
  notes: z.string(),
  jobId: z.string(),
})
export type TransportManifestEntry = z.infer<
  typeof _transportManifestEntrySchema
>

export interface TransportManifestOptions {
  signal?: AbortSignal
  patientId?: string
  timeZone?: string
}

export class TransportManifestAppointmentError extends Error {
  readonly appointmentId: string
  constructor(appointmentId: string, message: string) {
    super(message)
    this.name = 'TransportManifestAppointmentError'
    this.appointmentId = appointmentId
  }
}

export class TransportManifestAggregateError extends Error {
  readonly errors: TransportManifestAppointmentError[]
  constructor(errors: TransportManifestAppointmentError[]) {
    const count = errors.length
    super(`${count} appointment${count === 1 ? '' : 's'} could not be exported`)
    this.name = 'TransportManifestAggregateError'
    this.errors = errors
  }
}

// Iterate through each appointment requiring transport on the specified date and
// get the patient, location, and service allocatoin questionnaire response for linkId `schedule-and-transport`
// to build out a csv with the following columns:
// Date, Participant Identification Number, Participant Name, Participant Phone Number, Booking Purpose, Origin Address, Destination Address, Assistive Device, # of Passengers, Scheduled Pickup time, Appt time, Notes
// https://coda.io/d/Program-Development_d4RS-oLb0dh/silverride-IT-curves-system-demo_suJ27Ky-?searchClick=03218e2c-088c-4bc8-ad59-20c5511d8c2e_4RS-oLb0dh#_luj5-4BA
// The notes and assistive devices will map to the answer for `schedule-and-transport`
export const exportTransportManifest = async (
  medplum: SeenMedplumClient,
  date: TransportManifestDate,
  { signal, patientId, timeZone }: TransportManifestOptions = {}
): Promise<TransportManifestEntry[]> => {
  const clock = createTransportManifestClock(date, timeZone)
  const { start, end } = clock
  const formatTimeTransportManifest = (value: string) =>
    clock.format(value, 'HH:mm a')
  const formatDateTransportManifest = (value: string) =>
    clock.format(value, 'yyyy-MM-dd')

  const appointmentsByReference: Record<string, ExtendedAppointment> = {}

  const appointmentsSearchParams = new URLSearchParams()
  appointmentsSearchParams.set(
    '_tag',
    `${codeSystems.REQUIRES_TRANSPORTATION}|`
  )
  // Use append to not overwrite the first date param
  appointmentsSearchParams.append('date', `ge${start}`)
  appointmentsSearchParams.append('date', `le${end}`)
  appointmentsSearchParams.set('status', 'booked')
  appointmentsSearchParams.set('_count', '100')
  appointmentsSearchParams.set('_sort', '-_lastUpdated')
  if (patientId) {
    appointmentsSearchParams.set('patient', `Patient/${patientId}`)
  }

  for await (const page of medplum.searchResourcePages(
    'Appointment',
    appointmentsSearchParams.toString(),
    { signal }
  )) {
    for (const appointment of page) {
      appointmentsByReference[getReferenceString(appointment)] =
        ExtendedAppointment.fromFhirResource(appointment)
    }
  }

  const appointmentErrors: TransportManifestAppointmentError[] = []

  const patientReferences = compact(
    Object.values(appointmentsByReference).map((a) => {
      const patientReference = a.patients[0]
      if (!patientReference) {
        appointmentErrors.push(
          new TransportManifestAppointmentError(
            a.id ?? '',
            `Patient reference not found in ${a.reference.reference}`
          )
        )
        return undefined
      }
      return patientReference
    })
  )
  const locationReferences = Object.values(appointmentsByReference).flatMap(
    (a) => compact([a.location, a.pickupLocation])
  )

  const [patientsByReference, locationsByReference] = await Promise.all([
    hydrateReferenceMap(medplum, patientReferences, { signal }),
    hydrateReferenceMap(medplum, locationReferences, { signal }),
  ])

  const questionnaireResponsesByPatientReference: Record<
    string,
    QuestionnaireResponse
  > = {}

  for (const patientReferenceChunk of chunk(patientReferences, 100)) {
    for (const questionnaireResponse of await medplum.searchResources(
      'QuestionnaireResponse',
      normalizeQuery({
        subject: patientReferenceChunk,
        questionnaire: ServiceAllocationQuestionnaire.url,
      }),
      { signal }
    )) {
      const { subject } = questionnaireResponse
      invariant(
        subject,
        `Subject not found in questionnaire response ${questionnaireResponse.id}`
      )

      const questionnaireResponsePatientReference = getReferenceString(subject)
      questionnaireResponsesByPatientReference[
        questionnaireResponsePatientReference
      ] = questionnaireResponse
    }
  }

  const allAppointments = Object.values(appointmentsByReference)
  const deliveryAppointments = allAppointments.filter((a) => a.isDelivery)
  const regularAppointments = allAppointments.filter((a) => !a.isDelivery)

  // Delivery trips: pickup → dropoff, 0 passengers, "Delivery-" name prefix
  const deliveryTrips: TransportManifestEntry[] = []
  for (const appointment of deliveryAppointments) {
    const patientReferenceString = appointment.patients[0]?.reference
    if (!patientReferenceString) {
      console.warn(
        `Delivery appointment ${appointment.id} missing patient reference, skipping`
      )
      continue
    }
    const patient = patientsByReference[patientReferenceString]
    if (!patient) {
      continue
    }
    const patientName = officialNameOrFallback(patient)

    const pickupLocation = appointment.pickupLocation?.reference
      ? locationsByReference[appointment.pickupLocation.reference]
      : undefined
    const dropoffLocation = appointment.dropoffLocation?.reference
      ? locationsByReference[appointment.dropoffLocation.reference]
      : undefined

    if (!pickupLocation || !dropoffLocation) {
      console.warn(
        `Delivery appointment ${appointment.id} missing ${[!pickupLocation && 'pickup', !dropoffLocation && 'dropoff'].filter(Boolean).join(' and ')} location`
      )
      continue
    }

    deliveryTrips.push({
      date: appointment.start
        ? formatDateTransportManifest(appointment.start)
        : '',
      participantId: patient.id ?? '',
      participantName: patientName
        ? `Delivery-${formatHumanName(patientName)}`
        : 'Delivery-Unknown',
      participantPhone: normalizePhone(getPrimaryPhoneNumber(patient)),
      appointmentPurpose: appointment.reason ?? '',
      originAddress: pickupLocation?.address?.line?.[0] ?? '',
      originUnit: pickupLocation?.address?.line?.[1] ?? '',
      originCity: pickupLocation?.address?.city ?? '',
      originState: pickupLocation?.address?.state ?? '',
      originZip: normalizeZipCode(pickupLocation?.address?.postalCode),
      destinationAddress: dropoffLocation?.address?.line?.[0] ?? '',
      destinationUnit: dropoffLocation?.address?.line?.[1] ?? '',
      destinationCity: dropoffLocation?.address?.city ?? '',
      destinationState: dropoffLocation?.address?.state ?? '',
      destinationZip: normalizeZipCode(dropoffLocation?.address?.postalCode),
      assistiveDevice: '',
      wheelchairCount: 0,
      passengerCount: 0,
      scheduledPickupTime: '',
      appointmentTime: appointment.start
        ? formatTimeTransportManifest(appointment.start)
        : '',
      notes: appointment.notes?.trim() ?? '',
      jobId: `${patient.id}-${appointment.start ? clock.format(appointment.start, 'yyyyMMdd-HHmm') : ''}-DELIVERY`,
    })
  }

  const groupableRegularAppointments = regularAppointments.filter(
    (appointment) => {
      const patientReferenceString = appointment.patients[0]?.reference
      if (!patientReferenceString) {
        appointmentErrors.push(
          new TransportManifestAppointmentError(
            appointment.id ?? '',
            `Patient reference not found in appointment ${appointment.id}`
          )
        )
        return false
      }
      return true
    }
  )

  const patientGroupedAppointments: Record<string, ExtendedAppointment[]> =
    groupBy(
      groupableRegularAppointments,
      (appointment) => appointment.patients[0].reference as string
    )

  const regularTrips = Object.values(patientGroupedAppointments).flatMap(
    (appointments) => {
      const sortedAppointments = sortBy(appointments, (a) => a.start)
      const firstAppointment = sortedAppointments.at(0)

      const pushErr = (appointmentId: string, message: string): void => {
        appointmentErrors.push(
          new TransportManifestAppointmentError(appointmentId, message)
        )
      }

      if (!firstAppointment) {
        pushErr('', 'No appointments to process for patient group')
        return []
      }

      const firstAppointmentId = firstAppointment.id ?? ''

      const startTime = firstAppointment.start
      if (!startTime) {
        pushErr(firstAppointmentId, 'Invalid appointment range')
        return []
      }

      const latestEndingAppointment = maxBy(
        appointments.filter((a) => a.end),
        (a) => a.end
      )
      if (!latestEndingAppointment?.end) {
        pushErr(firstAppointmentId, 'No appointment with end time found')
        return []
      }

      const patientReferenceString = firstAppointment.patients[0].reference
      if (!patientReferenceString) {
        pushErr(firstAppointmentId, 'Missing patient')
        return []
      }
      const patient = patientsByReference[patientReferenceString]
      if (!patient) {
        pushErr(firstAppointmentId, 'Missing patient')
        return []
      }
      const patientAddress = homeAddressOrFallback(patient)
      if (!patientAddress) {
        pushErr(firstAppointmentId, 'Missing patient address')
        return []
      }

      const participant = Participant.fromFhirResource(patient)
      // Feeds IT Curves' nTotalWheelChairs: wheelchair spaces for vehicle
      // capacity.
      const wheelchairCount = participant.hasFlag(
        patients.PARTICIPANT_FLAGS.wheelchair.coding
      )
        ? 1
        : 0

      const questionnaireResponse =
        questionnaireResponsesByPatientReference[patientReferenceString]
      const globalScheduleAndTransportNote = tryGetQuestionnaireAnswers(
        questionnaireResponse
      )?.['schedule-and-transport']?.valueString

      const notes = compact(
        uniq(sortedAppointments.map((a) => a.notes?.trim()))
      ).join('\n')

      const name = officialNameOrFallback(patient)
      const jobIdPrefix = `${patient.id}-${clock.format(startTime, 'yyyyMMdd')}`

      const shared: Pick<
        TransportManifestEntry,
        | 'date'
        | 'participantId'
        | 'participantName'
        | 'participantPhone'
        | 'passengerCount'
        | 'wheelchairCount'
        | 'assistiveDevice'
        | 'notes'
      > = {
        date: formatDateTransportManifest(startTime),
        participantId: patient.id ?? '',
        participantName: name ? formatHumanName(name) : '',
        participantPhone: normalizePhone(getPrimaryPhoneNumber(patient)),
        passengerCount: 1,
        wheelchairCount,
        assistiveDevice: globalScheduleAndTransportNote ?? '',
        notes: notes ?? '',
      }

      const addressFields = (
        addr:
          | {
              line?: string[]
              city?: string
              state?: string
              postalCode?: string
            }
          | undefined,
        prefix: 'origin' | 'destination'
      ) => ({
        [`${prefix}Address`]: addr?.line?.[0] ?? '',
        [`${prefix}Unit`]: addr?.line?.[1] ?? '',
        [`${prefix}City`]: addr?.city ?? '',
        [`${prefix}State`]: addr?.state ?? '',
        [`${prefix}Zip`]: normalizeZipCode(addr?.postalCode),
      })

      /**
       * Resolve the location for an appointment. On failure, record the error
       * (attributed to the specific appointment) and return undefined so
       * sibling appointments in the same patient group can still be checked.
       */
      const getAppointmentLocation = (
        appt: ExtendedAppointment
      ): Location | undefined => {
        const apptId = appt.id ?? ''
        const locRef = appt.location?.reference
        if (!locRef) {
          pushErr(apptId, 'Location missing')
          return undefined
        }
        const loc = locationsByReference[locRef]
        if (!loc?.address) {
          pushErr(apptId, 'Location address missing')
          return undefined
        }
        return loc
      }

      const trips: TransportManifestEntry[] = []
      let tripIndex = 0

      // Generate trip labels: A, B, C, ... Z, AA, AB, etc.
      const getTripLabel = (index: number): string => {
        if (index < 26) {
          return String.fromCharCode(65 + index)
        }
        let label = ''
        let n = index + 1
        while (n > 0) {
          n -= 1
          label = String.fromCharCode(65 + (n % 26)) + label
          n = Math.floor(n / 26)
        }
        return label
      }

      // Resolve every appointment's location up front so all location errors
      // surface in a single export attempt (not just the first failure).
      // Only resolved locations are stored; missing ones are tracked separately.
      const locationsByAppointmentId = new Map<string, Location>()
      let anyMissingLocation = false
      for (const appt of sortedAppointments) {
        const loc = getAppointmentLocation(appt)
        if (loc) {
          locationsByAppointmentId.set(appt.id ?? '', loc)
        } else {
          anyMissingLocation = true
        }
      }

      // If any appointment in this patient's group is missing location data,
      // skip trip generation for the group — errors already recorded above.
      if (anyMissingLocation) {
        return []
      }

      const getRequiredLocation = (appt: ExtendedAppointment): Location => {
        const loc = locationsByAppointmentId.get(appt.id ?? '')
        invariant(
          loc,
          `Location missing for appointment ${appt.id} after resolution`
        )
        return loc
      }

      // Every leg is dropped when origin and destination are the same building.
      const getAddressKey = (addr: Location['address']) =>
        buildingAddressKey(addr) ?? ''
      const homeAddrKey = getAddressKey(patientAddress)

      // Trip 1: Home → First appointment location
      const firstLocation = getRequiredLocation(firstAppointment)
      if (homeAddrKey !== getAddressKey(firstLocation.address)) {
        trips.push({
          ...shared,
          appointmentPurpose: firstAppointment.reason ?? '',
          ...addressFields(patientAddress, 'origin'),
          ...addressFields(firstLocation.address, 'destination'),
          scheduledPickupTime: '',
          appointmentTime: firstAppointment.start
            ? formatTimeTransportManifest(firstAppointment.start)
            : '',
          jobId: `${jobIdPrefix}-${getTripLabel(tripIndex++)}`,
        } as TransportManifestEntry)
      }

      /* Track the participant's physical location so return-to-day-center trips
         don't produce duplicates when the next appointment is back at the
         day center. */
      let pptLocation = firstLocation
      let pptAddrKey = getAddressKey(pptLocation.address)

      // Middle trips: Between appointments when address changes
      for (let i = 1; i < sortedAppointments.length; i++) {
        const prevAppt = sortedAppointments[i - 1]
        const currAppt = sortedAppointments[i]
        const currLocation = getRequiredLocation(currAppt)
        const currAddrKey = getAddressKey(currLocation.address)

        if (pptAddrKey === currAddrKey) {
          continue
        }

        trips.push({
          ...shared,
          appointmentPurpose: currAppt.reason ?? '',
          ...addressFields(pptLocation.address, 'origin'),
          ...addressFields(currLocation.address, 'destination'),
          scheduledPickupTime:
            prevAppt.end && (!currAppt.start || prevAppt.end <= currAppt.start)
              ? formatTimeTransportManifest(prevAppt.end)
              : '',
          appointmentTime: currAppt.start
            ? formatTimeTransportManifest(currAppt.start)
            : '',
          jobId: `${jobIdPrefix}-${getTripLabel(tripIndex++)}`,
        } as TransportManifestEntry)

        pptLocation = currLocation
        pptAddrKey = currAddrKey

        // Return to day center: Check if ANY earlier appointment is a day center
        // that started before and ends after the current appointment, with a different address
        const overlappingDayCenter = sortedAppointments.find((appt, idx) => {
          if (idx >= i) {
            return false
          }
          if (!appt.isDayCenter) {
            return false
          }
          if (!appt.start || !currAppt.start) {
            return false
          }
          if (appt.start >= currAppt.start) {
            return false
          }
          if (!appt.end || !currAppt.end) {
            return false
          }
          if (appt.end <= currAppt.end) {
            return false
          }
          const apptLocation = locationsByAppointmentId.get(appt.id ?? '')
          if (!apptLocation) {
            return false
          }
          return getAddressKey(apptLocation.address) !== currAddrKey
        })

        if (overlappingDayCenter) {
          const dayCenterLocation = getRequiredLocation(overlappingDayCenter)
          trips.push({
            ...shared,
            appointmentPurpose: overlappingDayCenter.reason ?? '',
            ...addressFields(currLocation.address, 'origin'),
            ...addressFields(dayCenterLocation.address, 'destination'),
            scheduledPickupTime: currAppt.end
              ? formatTimeTransportManifest(currAppt.end)
              : '',
            appointmentTime: '',
            jobId: `${jobIdPrefix}-${getTripLabel(tripIndex++)}`,
          } as TransportManifestEntry)

          pptLocation = dayCenterLocation
          pptAddrKey = getAddressKey(dayCenterLocation.address)
        }
      }

      // Final trip: Location of latest-ending appointment → Home
      const lastLocation = getRequiredLocation(latestEndingAppointment)
      if (getAddressKey(lastLocation.address) !== homeAddrKey) {
        trips.push({
          ...shared,
          appointmentPurpose: latestEndingAppointment.reason ?? '',
          ...addressFields(lastLocation.address, 'origin'),
          ...addressFields(patientAddress, 'destination'),
          scheduledPickupTime: formatTimeTransportManifest(
            latestEndingAppointment.end
          ),
          appointmentTime: '',
          jobId: `${jobIdPrefix}-${getTripLabel(tripIndex++)}`,
        } as TransportManifestEntry)
      }

      return trips
    }
  )

  if (appointmentErrors.length > 0) {
    throw new TransportManifestAggregateError(appointmentErrors)
  }

  return [...deliveryTrips, ...regularTrips]
}

async function hydrateReferenceMap<T extends Resource>(
  medplum: SeenMedplumClient,
  references: Reference<T>[],
  { signal, chunkSize = 100 }: { signal?: AbortSignal; chunkSize?: number } = {}
): Promise<Record<string, T>> {
  const resourceType = getType(references[0])
  if (!resourceType) {
    return {}
  }

  const result: Record<string, T> = {}

  for (const referenceChunk of chunk(references, chunkSize)) {
    for (const resource of await medplum.searchResources(
      resourceType,
      normalizeQuery({
        _id: referenceChunk.map(resolveId),
        _count: chunkSize,
      }),
      { signal }
    )) {
      result[getReferenceString(resource)] = resource as T
    }
  }

  return result
}
